/**
 * ImportDeckModal - Import a deck list from file or text
 *
 * Uses shared useImportSteps for 4-step flow:
 *   input → preview → importing → complete
 *
 * Two modes:
 * - Direct import (onImport): adds cards to existing deck, preview shows zone selector
 * - Create deck (onCreateDeck): preview shows deck form + card matching table
 *
 * DeckImportPreviewModal content is merged into the preview step.
 */
/**
 * ImportDeckModal - Import a deck list from file or text
 *
 * Uses shared useImportSteps for 4-step flow:
 *   input → preview → importing → complete
 *
 * Two modes:
 * - Direct import (onImport): adds cards to existing deck, preview shows zone selector
 * - Create deck (onCreateDeck): preview shows deck form + card matching table
 *
 * DeckImportPreviewModal content is merged into the preview step.
 */
import type { ChangeEvent, ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { FaArrowRight, FaCheckCircle, FaExclamationTriangle, FaFileImport, FaInfoCircle } from 'react-icons/fa';

import Button from '@/components/Button/Button';
import Dropdown, { type DropdownOption } from '@/components/Dropdown/Dropdown';
import Modal from '@/components/Modal/Modal';
import Spinner from '@/components/Spinner/Spinner';
import { detectDeckFormat, parseDeckList, type DeckImportCard, type DeckImportResult } from '@/features/decks/lib/deckImportExport';
import FileDropzone from '@/features/import-export/components/FileDropzone';
import ImportTabs from '@/features/import-export/components/ImportTabs';
import PrintingPreferenceToggle from '@/features/import-export/components/PrintingPreferenceToggle';
import SupportedFormatsInfo from '@/features/import-export/components/SupportedFormatsInfo';
import type { PrintingPreference } from '@/features/import-export/lib/formatAdapters';
import { listBackupToDeckCards } from '@/features/import-export/lib/formatAdapters';
import { isMtgCraftBackup, parseDeckBackup, parseListBackup } from '@/features/import-export/parsers/mtgcraft';
import { detectFormatFull } from '@/features/import-export/parsers/parseImport';
import type { ImportTabId } from '@/features/import-export/types';
import { isMultiListBackup, isSingleListBackup } from '@/features/import-export/types';
import useCardIndex from '@/hooks/useCardIndex';
import useCollectionInventory from '@/hooks/useCollectionInventory';
import useImportSteps from '@/hooks/useImportSteps';
import { DECK_FORMAT_LABELS, DECK_STATUS_LABELS } from '@/lib/deckUtils';
import { isTokenOrEmblem } from '@/lib/deckValidation';
import type { FormatHints } from '@/lib/formatDetection';
import { parseUniversalCSV } from '@/lib/universalCSVParser';
import type { DeckFormat, DeckStatus, DeckZone } from '@/types/deck';

import DeckImportPreviewTable, { type DeckImportPreviewCard } from './DeckImportPreviewTable';
export type { DeckImportPreviewCard } from './DeckImportPreviewTable';

// ============================================================================
// Types
// ============================================================================

export interface DeckFormData {
  name: string;
  format: DeckFormat | null;
  status: DeckStatus;
  description: string | null;
}

export interface ImportDeckModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Direct import (used by DeckEditorPage to add cards to existing deck) */
  onImport?: (result: DeckImportResult) => Promise<void>;
  /** Create new deck with preview (used by DecksPage) - when provided, shows preview modal */
  onCreateDeck?: (deckData: DeckFormData, cards: DeckImportPreviewCard[]) => Promise<void>;
}

const SUPPORTED_FORMATS = [
  'MtgCraft Backup',
  'MtgCraft List Backup',
  'Dragon Shield CSV',
  'Delver Lens CSV',
  'MTGA',
  'Moxfield',
  'Simple Text',
];

const IMPORT_STEPS = ['input', 'preview', 'importing', 'complete'] as const;

const TARGET_ZONE_OPTIONS: DropdownOption<DeckZone>[] = [
  { value: 'main', label: 'Main Deck' },
  { value: 'sideboard', label: 'Sideboard' },
];

const FORMAT_OPTIONS: { value: DeckFormat | ''; label: string }[] = [
  { value: '', label: 'No Format' },
  ...Object.entries(DECK_FORMAT_LABELS).map(([value, label]) => ({ value: value as DeckFormat, label })),
];

const STATUS_OPTIONS: { value: DeckStatus; label: string }[] = Object.entries(DECK_STATUS_LABELS).map(([value, label]) => ({
  value: value as DeckStatus,
  label,
}));

// ============================================================================
// Component
// ============================================================================

export default function ImportDeckModal({ isOpen, onClose, onImport, onCreateDeck }: ImportDeckModalProps): ReactElement {
  const steps = useImportSteps(IMPORT_STEPS);

  // Input state
  const [activeTab, setActiveTab] = useState<ImportTabId>('upload');
  const [text, setText] = useState('');
  const [parseResult, setParseResult] = useState<DeckImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detectedFormat, setDetectedFormat] = useState<string | null>(null);
  const [targetZone, setTargetZone] = useState<DeckZone>('main');
  const [crossImportInfo, setCrossImportInfo] = useState<string | null>(null);
  const [formatHints, setFormatHints] = useState<FormatHints | undefined>(undefined);

  // Preview / deck form state (merged from DeckImportPreviewModal)
  const [deckName, setDeckName] = useState('Imported Deck');
  const [deckFormat, setDeckFormat] = useState<DeckFormat | null>(null);
  const [deckStatus, setDeckStatus] = useState<DeckStatus>('brewing');
  const [deckDescription, setDeckDescription] = useState('');
  const [printingPreference, setPrintingPreference] = useState<PrintingPreference>('exact');

  // Progress / result state
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResult, setImportResult] = useState({ success: 0, failed: 0 });

  // Hooks
  const { getCardById, getCardByName, getCardByOracleId, getCardBySetNumber } = useCardIndex();
  const inventory = useCollectionInventory();

  const usePreviewFlow = !!onCreateDeck;

  // Process text content, returns true if valid result
  const processContent = useCallback(
    (content: string): boolean => {
      setText(content);
      setError(null);
      setCrossImportInfo(null);

      const trimmed = content.trim();
      if (!trimmed) {
        setParseResult(null);
        setDetectedFormat(null);
        return false;
      }

      if (isMtgCraftBackup(trimmed)) {
        try {
          const parsed = JSON.parse(trimmed);
          if (isSingleListBackup(parsed) || isMultiListBackup(parsed)) {
            const backupResult = parseListBackup(trimmed);
            if (backupResult.lists.length > 0) {
              const cardIndex = { getCardById, getCardByName, getCardByOracleId };
              const allListCards = backupResult.lists.flatMap((l) => l.cards);
              const deckCards = listBackupToDeckCards(allListCards, cardIndex);
              const result: DeckImportResult = {
                name: backupResult.lists.length === 1 ? backupResult.lists[0].name : null,
                format: null,
                cards: deckCards,
                errors: backupResult.errors,
                hasZoneDefinitions: false,
              };
              setParseResult(result);
              setDetectedFormat('MtgCraft List Backup');
              setCrossImportInfo('Detected MtgCraft list backup — importing all cards to Main Deck.');
              return result.cards.length > 0;
            }
          }
        } catch {
          // Not a list backup, try deck backup below
        }

        const backupResult = parseDeckBackup(trimmed);
        if (backupResult.decks.length > 0) {
          const deck = backupResult.decks[0];
          const cards = deck.cards.map((c) => ({
            name: '',
            quantity: c.quantity,
            zone: c.zone ?? 'main',
            setCode: null,
            collectorNumber: null,
            scryfallId: c.scryfall_id,
          }));
          const hasZoneDefinitions = cards.some((c) => c.zone !== 'main');
          const result: DeckImportResult = {
            name: deck.name,
            format: deck.format ?? null,
            cards,
            errors: backupResult.errors,
            hasZoneDefinitions,
          };
          setParseResult(result);
          setDetectedFormat('MtgCraft Backup');
          return result.cards.length > 0;
        } else if (backupResult.errors.length > 0) {
          setError(backupResult.errors.join('\n'));
          setParseResult(null);
          setDetectedFormat(null);
          return false;
        }
      }

      const detected = detectFormatFull(trimmed, 'deck');
      if (detected && detected.inputType === 'csv') {
        const csvResult = parseUniversalCSV(trimmed, detected.source);
        if (csvResult.cards.length > 0) {
          const deckCards: DeckImportCard[] = csvResult.cards.map((c) => ({
            name: c.name,
            setCode: c.setCode,
            collectorNumber: c.collectorNumber,
            quantity: c.quantity,
            zone: 'main' as const,
          }));
          const result: DeckImportResult = {
            name: null,
            format: null,
            cards: deckCards,
            errors: [],
            hasZoneDefinitions: false,
          };
          setParseResult(result);
          setDetectedFormat(detected.format === 'dragonshield-deck-csv' ? 'Dragon Shield CSV' : 'Delver Lens CSV');
          setFormatHints(detected.hints);
          return result.cards.length > 0;
        }
      }

      const result = parseDeckList(trimmed);
      setParseResult(result);
      setDetectedFormat(detectDeckFormat(trimmed));

      if (detected && detected.inputType === 'text' && detected.hints) {
        setFormatHints(detected.hints);
      } else {
        setFormatHints(undefined);
      }

      return result.cards.length > 0;
    },
    [getCardById, getCardByName, getCardByOracleId],
  );

  const handleTextChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      processContent(e.target.value);
    },
    [processContent],
  );

  // Continue to preview step — pre-fill deck form from parse result
  const handleContinue = useCallback(() => {
    if (!parseResult || parseResult.cards.length === 0) return;
    setDeckName(formatHints?.suggestedName ?? parseResult.name ?? 'Imported Deck');
    setDeckFormat(formatHints?.deckFormat ?? (parseResult.format as DeckFormat) ?? null);
    setDeckStatus('brewing');
    setDeckDescription('');
    setPrintingPreference('exact');
    steps.goTo('preview');
  }, [parseResult, formatHints, steps]);

  const handleFileDrop = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (content) {
          const hasValidCards = processContent(content);
          if (hasValidCards && usePreviewFlow) {
            // Auto-continue: pre-fill deck form and go to preview
            // Use setTimeout to ensure parseResult is set
            setTimeout(() => {
              setDeckName(formatHints?.suggestedName ?? 'Imported Deck');
              setDeckFormat(formatHints?.deckFormat ?? null);
              setDeckStatus('brewing');
              setDeckDescription('');
              setPrintingPreference('exact');
              steps.goTo('preview');
            }, 0);
          }
        }
      };
      reader.onerror = () => {
        setError('Failed to read file');
      };
      reader.readAsText(file);
    },
    [processContent, usePreviewFlow, formatHints, steps],
  );

  // Direct import (adding to existing deck)
  const handleDirectImport = useCallback(async () => {
    if (!parseResult || parseResult.cards.length === 0 || !onImport) return;

    const resultToImport: DeckImportResult = parseResult.hasZoneDefinitions
      ? parseResult
      : { ...parseResult, cards: parseResult.cards.map((c) => ({ ...c, zone: targetZone })) };

    const total = resultToImport.cards.reduce((sum, c) => sum + c.quantity, 0);
    steps.goTo('importing');
    setImportProgress({ current: 0, total });

    try {
      await onImport(resultToImport);
      setImportResult({ success: total, failed: 0 });
      steps.goTo('complete');
    } catch (err) {
      console.error('Import failed:', err);
      setError(err instanceof Error ? err.message : 'Import failed');
      steps.goTo('input');
    }
  }, [parseResult, onImport, targetZone, steps]);

  // Match cards for preview (merged from DeckImportPreviewModal)
  const previewCards = useMemo((): DeckImportPreviewCard[] => {
    if (!parseResult) return [];
    return parseResult.cards.map((card) => {
      let matched = null;
      let matchType: 'exact' | 'name' | 'fuzzy' | 'none' = 'none';
      let versionSubstituted = false;
      const requestedSetCode = card.setCode;
      const requestedCollectorNumber = card.collectorNumber;

      if (card.scryfallId) {
        matched = getCardById(card.scryfallId);
        if (matched) matchType = 'exact';
      }

      if (!matched && card.setCode && card.collectorNumber) {
        matched = getCardBySetNumber(card.setCode, card.collectorNumber);
        if (matched) matchType = 'exact';
      }

      if (printingPreference === 'collection') {
        if (!matched || (matched && !inventory.hasExact(matched.set, matched.collector_number))) {
          const ownedPrinting = inventory.getOwnedPrinting(card.name);
          if (ownedPrinting) {
            const ownedCard = getCardBySetNumber(ownedPrinting.set, ownedPrinting.collector_number);
            if (ownedCard) {
              if (requestedSetCode && requestedCollectorNumber) {
                const wasRequested =
                  requestedSetCode.toLowerCase() === ownedCard.set && requestedCollectorNumber === ownedCard.collector_number;
                versionSubstituted = !wasRequested;
              }
              matched = ownedCard;
              matchType = 'name';
            }
          }
        }
      }

      if (!matched) {
        matched = getCardByName(card.name);
        if (matched) {
          matchType = 'name';
          if (requestedSetCode && requestedCollectorNumber) versionSubstituted = true;
        }
      }

      const hasExactVersion = matched ? inventory.hasExact(matched.set, matched.collector_number) : false;
      const hasAnyVersionRaw = matched ? inventory.hasAny(matched.name) : false;
      const effectiveZone = matched && isTokenOrEmblem(matched) ? 'tokens' : card.zone;

      return {
        ...card,
        zone: effectiveZone,
        matched: matched ?? null,
        matchType,
        hasExactVersion,
        hasAnyVersion: hasAnyVersionRaw && !hasExactVersion,
        ownedExactQty: matched ? inventory.getExactQty(matched.set, matched.collector_number) : 0,
        ownedTotalQty: matched ? inventory.getTotalQty(matched.name) : 0,
        versionSubstituted,
        requestedSetCode,
        requestedCollectorNumber,
      };
    });
  }, [parseResult, inventory, getCardByName, getCardBySetNumber, getCardById, printingPreference]);

  // Preview stats (for create-deck flow)
  const previewStats = useMemo(() => {
    const totalCards = previewCards.reduce((sum, c) => sum + c.quantity, 0);
    const uniqueCards = previewCards.length;
    const matchedCards = previewCards.filter((c) => c.matched).length;
    const ownedAny = previewCards.filter((c) => c.hasExactVersion || c.hasAnyVersion).length;
    const missing = previewCards.filter((c) => c.matched && !c.hasExactVersion && !c.hasAnyVersion).length;
    const notFound = previewCards.filter((c) => !c.matched).length;
    return { totalCards, uniqueCards, matchedCards, ownedAny, missing, notFound };
  }, [previewCards]);

  // Create deck from preview
  const handleCreateDeck = useCallback(async () => {
    if (!onCreateDeck || previewStats.matchedCards === 0) return;

    steps.goTo('importing');
    setImportProgress({ current: 0, total: previewStats.totalCards });

    try {
      await onCreateDeck(
        {
          name: deckName.trim() || 'Imported Deck',
          format: deckFormat,
          status: deckStatus,
          description: deckDescription.trim() || null,
        },
        previewCards,
      );
      setImportResult({ success: previewStats.totalCards, failed: previewStats.notFound });
      steps.goTo('complete');
    } catch (err) {
      console.error('Failed to create deck:', err);
      setError(err instanceof Error ? err.message : 'Failed to create deck');
      steps.goTo('preview');
    }
  }, [onCreateDeck, previewCards, previewStats, deckName, deckFormat, deckStatus, deckDescription, steps]);

  const resetState = useCallback(() => {
    steps.reset();
    setText('');
    setParseResult(null);
    setDetectedFormat(null);
    setError(null);
    setActiveTab('upload');
    setTargetZone('main');
    setCrossImportInfo(null);
    setFormatHints(undefined);
    setDeckName('Imported Deck');
    setDeckFormat(null);
    setDeckStatus('brewing');
    setDeckDescription('');
    setPrintingPreference('exact');
    setImportProgress({ current: 0, total: 0 });
    setImportResult({ success: 0, failed: 0 });
  }, [steps]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const totalCards = parseResult?.cards.reduce((sum, c) => sum + c.quantity, 0) ?? 0;
  const uniqueCards = parseResult?.cards.length ?? 0;

  // Upload tab content
  const uploadContent = (
    <div className="space-y-4">
      <FileDropzone onFileDrop={handleFileDrop} accept=".txt,.json,.csv" description="Supports TXT, JSON, and CSV deck files" />
      <SupportedFormatsInfo formats={SUPPORTED_FORMATS} />
    </div>
  );

  // Paste tab content
  const pasteContent = (
    <div className="space-y-4">
      <textarea
        value={text}
        onChange={handleTextChange}
        placeholder={`Paste your card list here...

Example MTGA format:
4 Lightning Bolt (M10) 146
2 Counterspell (MH2) 46

Or paste CSV content directly.`}
        rows={10}
        className="border-storm bg-slate/50 text-pearl placeholder-ash focus:border-aether w-full resize-y rounded-lg border px-3 py-2 font-mono text-sm focus:outline-none"
      />
      <SupportedFormatsInfo formats={SUPPORTED_FORMATS} />
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Cards"
      size={steps.isStep('preview') && usePreviewFlow ? 'xl' : 'lg'}
      showCloseButton={!steps.isStep('importing')}
    >
      {/* Step: Input */}
      {steps.isStep('input') && (
        <div className="space-y-5">
          <ImportTabs activeTab={activeTab} onTabChange={setActiveTab} uploadContent={uploadContent} pasteContent={pasteContent} />

          {/* Parse result */}
          {parseResult && (
            <div className="space-y-3">
              {crossImportInfo && (
                <div className="bg-aether/10 border-aether/30 flex items-start gap-3 rounded-lg border p-3">
                  <FaInfoCircle className="text-aether mt-0.5 h-4 w-4 shrink-0" />
                  <p className="text-silver text-sm">{crossImportInfo}</p>
                </div>
              )}

              <div className="text-ash flex items-center gap-2 text-sm">
                <span>Format detected:</span>
                <span className="bg-storm text-silver rounded px-2 py-0.5 text-xs uppercase">{detectedFormat}</span>
              </div>

              <div className="flex gap-4 text-sm">
                <div className="text-silver">
                  <span className="text-success font-semibold">{totalCards}</span> cards
                </div>
                <div className="text-silver">
                  <span className="text-success font-semibold">{uniqueCards}</span> unique
                </div>
                {parseResult.name && (
                  <div className="text-silver">
                    Name: <span className="font-semibold">{parseResult.name}</span>
                  </div>
                )}
              </div>

              {parseResult.hasZoneDefinitions && (
                <div className="flex flex-wrap gap-2">
                  {(['commander', 'companion', 'main', 'sideboard', 'maybeboard'] as const).map((zone) => {
                    const zoneCards = parseResult.cards.filter((c) => c.zone === zone);
                    const zoneTotal = zoneCards.reduce((sum, c) => sum + c.quantity, 0);
                    if (zoneTotal === 0) return null;
                    return (
                      <div key={zone} className="bg-slate text-silver rounded px-2 py-1 text-xs">
                        <span className="capitalize">{zone}</span>: <span className="font-medium">{zoneTotal}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {!usePreviewFlow && !parseResult.hasZoneDefinitions && (
                <div className="border-storm/50 bg-slate/30 flex items-center gap-3 rounded-lg border p-3">
                  <label className="text-silver text-sm">Add cards to:</label>
                  <Dropdown<DeckZone>
                    value={targetZone}
                    options={TARGET_ZONE_OPTIONS}
                    onChange={(zone) => zone && setTargetZone(zone)}
                    aria-label="Target zone"
                  />
                </div>
              )}

              {parseResult.errors.length > 0 && (
                <div className="border-warning/30 bg-warning/10 rounded-lg border p-3">
                  <div className="text-warning mb-2 flex items-center gap-2 text-sm font-medium">
                    <FaExclamationTriangle className="h-4 w-4" />
                    <span>{parseResult.errors.length} line(s) could not be parsed</span>
                  </div>
                  <ul className="text-warning/80 max-h-24 space-y-1 overflow-y-auto text-xs">
                    {parseResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="border-danger/30 bg-danger/10 rounded-lg border p-3">
              <p className="text-danger text-sm">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button text="Cancel" variant="text" onClick={handleClose} />
            {usePreviewFlow ? (
              <Button
                text={`Continue with ${totalCards > 0 ? `${totalCards} Cards` : 'Deck'}`}
                icon={<FaArrowRight />}
                onClick={handleContinue}
                disabled={!parseResult || parseResult.cards.length === 0}
              />
            ) : (
              <Button
                text={`Import ${totalCards > 0 ? `${totalCards} Cards` : 'Deck'}`}
                icon={<FaFileImport />}
                onClick={handleDirectImport}
                disabled={!parseResult || parseResult.cards.length === 0}
                color="create"
              />
            )}
          </div>
        </div>
      )}

      {/* Step: Preview (create-deck flow — merged from DeckImportPreviewModal) */}
      {steps.isStep('preview') && usePreviewFlow && parseResult && (
        <div className="space-y-5">
          {/* Deck form */}
          <div className="bg-slate/50 rounded-lg p-4">
            <h3 className="text-ash mb-3 text-xs font-semibold tracking-wide uppercase">Deck Information</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="deck-name" className="text-silver mb-1 block text-sm">
                  Name
                </label>
                <input
                  id="deck-name"
                  type="text"
                  value={deckName}
                  onChange={(e) => setDeckName(e.target.value)}
                  className="border-storm bg-obsidian text-pearl placeholder-ash focus:border-aether w-full rounded-lg border px-3 py-2 text-sm focus:outline-none"
                  placeholder="Deck name"
                />
              </div>
              <div>
                <label htmlFor="deck-format" className="text-silver mb-1 block text-sm">
                  Format
                </label>
                <select
                  id="deck-format"
                  value={deckFormat ?? ''}
                  onChange={(e) => setDeckFormat((e.target.value as DeckFormat) || null)}
                  className="border-storm bg-obsidian text-pearl focus:border-aether w-full rounded-lg border px-3 py-2 text-sm focus:outline-none"
                >
                  {FORMAT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-silver mb-1 block text-sm">Status</label>
                <div className="mt-1 flex gap-4">
                  {STATUS_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex cursor-pointer items-center gap-2">
                      <input
                        type="radio"
                        name="deck-status"
                        value={opt.value}
                        checked={deckStatus === opt.value}
                        onChange={(e) => setDeckStatus(e.target.value as DeckStatus)}
                        className="text-aether focus:ring-aether"
                      />
                      <span className="text-silver text-sm">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="deck-description" className="text-silver mb-1 block text-sm">
                  Description <span className="text-ash">(optional)</span>
                </label>
                <input
                  id="deck-description"
                  type="text"
                  value={deckDescription}
                  onChange={(e) => setDeckDescription(e.target.value)}
                  className="border-storm bg-obsidian text-pearl placeholder-ash focus:border-aether w-full rounded-lg border px-3 py-2 text-sm focus:outline-none"
                  placeholder="Brief description..."
                />
              </div>
            </div>
          </div>

          {/* Stats bar */}
          <div className="flex flex-wrap gap-2">
            <div className="bg-slate rounded-lg px-4 py-2">
              <span className="text-ash text-xs tracking-wide uppercase">Total</span>
              <p className="text-pearl text-lg font-semibold">{previewStats.totalCards}</p>
            </div>
            <div className="bg-slate rounded-lg px-4 py-2">
              <span className="text-ash text-xs tracking-wide uppercase">Unique</span>
              <p className="text-silver text-lg font-semibold">{previewStats.uniqueCards}</p>
            </div>
            <div className="bg-success/10 rounded-lg px-4 py-2">
              <span className="text-success/70 text-xs tracking-wide uppercase">Owned</span>
              <p className="text-success text-lg font-semibold">{previewStats.ownedAny}</p>
            </div>
            {previewStats.missing > 0 && (
              <div className="bg-warning/10 rounded-lg px-4 py-2">
                <span className="text-warning/70 text-xs tracking-wide uppercase">Missing</span>
                <p className="text-warning text-lg font-semibold">{previewStats.missing}</p>
              </div>
            )}
            {previewStats.notFound > 0 && (
              <div className="bg-danger/10 rounded-lg px-4 py-2">
                <span className="text-danger/70 text-xs tracking-wide uppercase">Not Found</span>
                <p className="text-danger text-lg font-semibold">{previewStats.notFound}</p>
              </div>
            )}
          </div>

          {/* Legend + printing preference */}
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="bg-success/20 border-success/30 inline-block h-3 w-3 rounded border" />
              <span className="text-ash">Exact printing owned</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="bg-aether/20 border-aether/30 inline-block h-3 w-3 rounded border" />
              <span className="text-ash">Different printing owned</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="bg-warning/20 border-warning/30 inline-block h-3 w-3 rounded border" />
              <span className="text-ash">Not in collection</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="bg-danger/20 border-danger/30 inline-block h-3 w-3 rounded border" />
              <span className="text-ash">Card not found</span>
            </div>
            <div className="ml-auto">
              <PrintingPreferenceToggle value={printingPreference} onChange={setPrintingPreference} />
            </div>
          </div>

          {/* Card table */}
          <DeckImportPreviewTable cards={previewCards} />

          {error && (
            <div className="border-danger/30 bg-danger/10 rounded-lg border p-3">
              <p className="text-danger text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="border-storm flex items-center justify-between border-t pt-4">
            <Button text="Back" variant="text" onClick={() => steps.goTo('input')} />
            <Button
              text="Create Deck & Open"
              icon={<FaFileImport />}
              color="create"
              onClick={handleCreateDeck}
              disabled={previewStats.matchedCards === 0}
            />
          </div>
        </div>
      )}

      {/* Step: Importing */}
      {steps.isStep('importing') && (
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner size="lg" />
          <p className="text-pearl mt-4 text-lg font-medium">{usePreviewFlow ? 'Creating deck...' : 'Importing cards...'}</p>
          <p className="text-ash mt-2">
            {importProgress.current} of {importProgress.total} cards
          </p>
          <div className="bg-slate mt-4 h-2 w-64 overflow-hidden rounded-full">
            <div
              className="bg-aether h-full transition-all duration-300"
              style={{
                width: `${importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Step: Complete */}
      {steps.isStep('complete') && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="bg-success/20 mb-4 rounded-full p-4">
            <FaCheckCircle className="text-success h-12 w-12" />
          </div>
          <h3 className="text-pearl text-xl font-semibold">{usePreviewFlow ? 'Deck Created!' : 'Import Complete!'}</h3>
          {importResult.success > 0 && (
            <p className="text-silver mt-2">
              {usePreviewFlow ? 'Created deck with' : 'Added'} {importResult.success} card{importResult.success !== 1 ? 's' : ''}
            </p>
          )}
          {importResult.failed > 0 && (
            <p className="text-danger mt-1 text-sm">
              {importResult.failed} card{importResult.failed !== 1 ? 's' : ''} could not be matched
            </p>
          )}
          <div className="mt-6 flex gap-3">
            <Button text="Import Another" variant="outlined" onClick={resetState} />
            <Button text="Done" onClick={handleClose} />
          </div>
        </div>
      )}
    </Modal>
  );
}
